import { useQuery, UseQueryResult, UseMutationResult, useQueryClient, useMutation } from '@tanstack/react-query';

import {
  CreateFreightOrderType,
  ArchiveFreightOrderType,
  ReturnFreightOrderType,
  QuoteFreightOrderType,
  QuoteFreightOrderTypeResponse,
  FreightPackageTypeResponse,
  FreightOrderByShipment,
} from '../@types/shipment.type';
import { getFreightPackageTypes, createFreightOrder, archiveFreightOrder, returnFreightOrder, quoteFreightOrder, freightOrderByShipment } from '../api/freight';
import { FreightPackageTypesKey, FreightOrderKey } from '../configs/freight';
import { ShipmentsKey } from '../configs/shipment';

export const useGetFreightPackageTypes = (): UseQueryResult<FreightPackageTypeResponse[]> => {
  return useQuery({
    queryKey: [FreightPackageTypesKey],
    queryFn: () => getFreightPackageTypes(),
    staleTime: 60 * 60 * 1000
  });
};

export const useCreateFreightOrder = (shipmentId?: number): UseMutationResult<void, Error, CreateFreightOrderType> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateFreightOrderType) => createFreightOrder(data),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [FreightOrderKey, shipmentId]
      });
      await queryClient.invalidateQueries({
        queryKey: [ShipmentsKey, shipmentId]
      });
    }
  });
};

export const useArchiveFreightOrder = (shipmentId?: number): UseMutationResult<void, Error, ArchiveFreightOrderType> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ArchiveFreightOrderType) => archiveFreightOrder(data),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [FreightOrderKey, shipmentId]
      });
    }
  });
};

export const useReturnFreightOrder = (shipmentId?: number): UseMutationResult<void, Error, ReturnFreightOrderType> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ReturnFreightOrderType) => returnFreightOrder(data),

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [FreightOrderKey, shipmentId]
      });
    }
  });
};

export const useQuoteFreightOrder = (): UseMutationResult<QuoteFreightOrderTypeResponse[], Error, QuoteFreightOrderType> => {
  return useMutation({
    mutationFn: (data: QuoteFreightOrderType) => quoteFreightOrder(data),
  });
};

export const useGetFreightOrdersByShipment = (shipmentId: number): UseQueryResult<FreightOrderByShipment[]> => {
  return useQuery({
    queryKey: [FreightOrderKey, shipmentId],
    queryFn: () => freightOrderByShipment(shipmentId)
  });
};